import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface TimePickerProps {
  value?: string;
  onChange: (time: string) => void;
  className?: string;
  placeholder?: string;
}

const TimePicker: React.FC<TimePickerProps> = ({ 
  value = '', 
  onChange, 
  className = '',
  placeholder = 'Select time'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [hours, setHours] = useState('00');
  const [minutes, setMinutes] = useState('00');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();
  
  // Parse initial value
  useEffect(() => {
    if (value) {
      const [h, m] = value.split(':');
      setHours(h || '00');
      setMinutes(m || '00');
    }
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleTimeChange = (newHours: string, newMinutes: string) => {
    setHours(newHours);
    setMinutes(newMinutes);
    onChange(`${newHours}:${newMinutes}`);
  };

  const formatTime = (time: string) => {
    return time.padStart(2, '0');
  };

  const displayValue = value ? `${formatTime(hours)}:${formatTime(minutes)}` : '';

  const inputStyle: React.CSSProperties = {
    backgroundColor: '#2a3038',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '8px 12px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: '38px',
    transition: 'background-color 0.2s'
  };

  const dropdownStyle: React.CSSProperties = {
    position: 'absolute',
    top: '100%',
    left: '0',
    right: '0',
    marginTop: '4px',
    backgroundColor: '#2a3038',
    border: '1px solid #4B5563',
    borderRadius: '4px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    zIndex: 50,
    padding: '16px'
  };

  const selectStyle: React.CSSProperties = {
    backgroundColor: '#4B5563',
    color: 'white',
    border: '1px solid #6B7280',
    borderRadius: '4px',
    padding: '4px 8px',
    textAlign: 'center',
    outline: 'none',
    minWidth: '60px'
  };

  const buttonStyle: React.CSSProperties = {
    backgroundColor: '#4B5563',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    padding: '4px 12px',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'background-color 0.2s'
  };

  const buttonHoverStyle: React.CSSProperties = {
    ...buttonStyle,
    backgroundColor: '#6B7280'
  };

  return (
    <div className={className} ref={dropdownRef} style={{ position: 'relative' }}>
      {/* Input Display */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        style={inputStyle}
        onMouseEnter={(e) => {
          (e.target as HTMLElement).style.backgroundColor = '#4B5563';
        }}
        onMouseLeave={(e) => {
          (e.target as HTMLElement).style.backgroundColor = '#2a3038';
        }}
      >
        <span style={{ color: displayValue ? 'white' : '#9CA3AF' }}>
          {displayValue || placeholder}
        </span>
        <svg 
          width="16" 
          height="16" 
          viewBox="0 0 24 24" 
          fill="none" 
          stroke="#9CA3AF" 
          strokeWidth="2"
          style={{ flexShrink: 0 }}
        >
          <circle cx="12" cy="12" r="10"/>
          <polyline points="12,6 12,12 16,14"/>
        </svg>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div style={dropdownStyle}>
          <div style={{direction: 'ltr', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
            {/* Hours */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <label style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '8px' }}>{t('hours')}</label>
              <select
                value={hours}
                onChange={(e) => handleTimeChange(e.target.value, minutes)}
                style={selectStyle}
                onFocus={(e) => {
                  (e.target as HTMLElement).style.outline = '2px solid #3B82F6';
                }}
                onBlur={(e) => {
                  (e.target as HTMLElement).style.outline = 'none';
                }}
              >
                {Array.from({ length: 24 }, (_, i) => (
                  <option key={i} value={formatTime(i.toString())}>
                    {formatTime(i.toString())}
                  </option>
                ))}
              </select>
            </div>

            {/* Separator */}
            <div style={{ color: 'white', fontSize: '20px', fontWeight: 'bold', marginTop: '24px' }}>:</div>

            {/* Minutes */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <label style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '8px' }}>{t('minutes')}</label>
              <select
                value={minutes}
                onChange={(e) => handleTimeChange(hours, e.target.value)}
                style={selectStyle}
                onFocus={(e) => {
                  (e.target as HTMLElement).style.outline = '2px solid #3B82F6';
                }}
                onBlur={(e) => {
                  (e.target as HTMLElement).style.outline = 'none';
                }}
              >
                {Array.from({ length: 60 }, (_, i) => (
                  <option key={i} value={formatTime(i.toString())}>
                    {formatTime(i.toString())}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Quick Time Buttons */}
          <div style={{ 
            marginTop: '16px', 
            display: 'grid', 
            gridTemplateColumns: 'repeat(3, 1fr)', 
            gap: '8px' 
          }}>
            {['09:00', '12:00', '14:00', '16:00', '18:00', '20:00'].map((time) => (
              <button
                key={time}
                onClick={() => {
                  const [h, m] = time.split(':');
                  handleTimeChange(h, m);
                  setIsOpen(false);
                }}
                style={buttonStyle}
                onMouseEnter={(e) => {
                  (e.target as HTMLElement).style.backgroundColor = '#6B7280';
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLElement).style.backgroundColor = '#4B5563';
                }}
              >
                {time}
              </button>
            ))}
          </div>

          {/* Action Buttons */}
          <div style={{ 
            marginTop: '16px', 
            display: 'flex', 
            justifyContent: 'flex-end', 
            gap: '8px' 
          }}>
            <button
              onClick={() => setIsOpen(false)}
              style={{ 
                padding: '4px 12px', 
                color: '#9CA3AF', 
                backgroundColor: 'transparent', 
                border: 'none', 
                cursor: 'pointer',
                transition: 'color 0.2s'
              }}
              onMouseEnter={(e) => {
                (e.target as HTMLElement).style.color = 'white';
              }}
              onMouseLeave={(e) => {
                (e.target as HTMLElement).style.color = '#9CA3AF';
              }}
            >
              {t('cancel')}
            </button>
            <button
              onClick={() => setIsOpen(false)}
              style={{
                padding: '4px 12px',
                backgroundColor: '#2563EB',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => {
                (e.target as HTMLElement).style.backgroundColor = '#1D4ED8';
              }}
              onMouseLeave={(e) => {
                (e.target as HTMLElement).style.backgroundColor = '#2563EB';
              }}
            >
              {t('done')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimePicker;
